import React, { forwardRef, useCallback } from "react";
import "./Services.css";
import celticContractorsLogo from "../assets/CelticContractors-Landscape-Reversed.webp";
import SpaRoundedIcon from "@mui/icons-material/SpaRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import SelfImprovementRoundedIcon from "@mui/icons-material/SelfImprovementRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import { useInView } from "../hooks/useInView";

const serviceItems = [
  {
    title: "Reiki Energy Healing",
    icon: SpaRoundedIcon,
    highlight: "Realign your body and aura",
    description:
      "Invite universal life-force energy to dissolve tension, clear energetic blockages, and restore deep harmony. Sessions blend Reiki with gentle sound and crystal support so you leave grounded, lighter, and at ease.",
  },
  {
    title: "Angelic Core Healing",
    icon: AutoAwesomeRoundedIcon,
    highlight: "Open to the Angelic realms",
    description:
      "In sacred partnership with the angels, layers of pain, fear, and old stories soften and release. Their luminous presence restores balance, awakens the sacred light within, and reconnects you with your true essence.",
  },
  {
    title: "Soul Renewal Guidance",
    icon: SelfImprovementRoundedIcon,
    highlight: "Receive loving direction",
    description:
      "Receive heart-led messages, intuitive mentoring, and practical steps that help you step into your highest vibration with grace and trust. Each session is tailored to the moment you are in, offering clarity and calm confidence.",
  },
  {
    title: "Psychic Mediumship Readings",
    icon: PsychologyRoundedIcon,
    highlight: "Reconnect with loved ones",
    description:
      "Experience compassionate, accurate communication with those in spirit. Private one-to-one sittings, intimate group gatherings, and home visits create a warm, personal space for validation, comfort, and the reassurance that love never ends.",
  },
];

const Services = forwardRef((_, ref) => {
  const [inViewRef, isInView] = useInView({ threshold: 0.1 });
  const setRef = useCallback(
    (el) => {
      inViewRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    },
    [ref, inViewRef]
  );

  return (
    <section
      className={`services-section ${isInView ? "services-in-view" : ""}`}
      ref={setRef}
    >
      <div className="services-wrapper">
        <header className="services-header">
          <p className="services-eyebrow">Guidance & Healing</p>
          <h2>Services</h2>
          <p>
            For more than <strong>18 years</strong>, Linda has walked the path
            of a healer, guided by intuition, compassion, and the luminous
            presence of the <strong>Divine</strong>. Every session is crafted
            with care so you feel safe, supported, and deeply connected to the
            wisdom of <strong>spirit</strong>.
          </p>
        </header>

        <div className="services-grid">
          {serviceItems.map(({ title, highlight, description, icon: Icon }, index) => (
            <article
              className="service-card"
              key={title}
              style={{ animationDelay: `${index * 0.12}s` }}
            >
              <div className="service-icon-wrap">
                <Icon className="service-icon" />
              </div>
              <div className="service-body">
                <h3>{title}</h3>
                <span className="service-highlight">{highlight}</span>
                <p>{description}</p>
              </div>
            </article>
          ))}
        </div>

        <p className="services-footnote">
          Sessions are available in person across Ireland and remotely when
          appropriate. Together we create a space where transformation feels
          gentle, sacred, and attainable.
        </p>

        <div className="partnership-banner">
          <p className="partnership-text">
            Partnering exclusively with <strong>Celtic Contractors</strong> for on-demand spiritual life coaching services.
          </p>
          <p className="partnership-subtext">
            Don't hesitate to enquire about similar services we can provide for your business.
          </p>
          <img
            src={celticContractorsLogo}
            alt="Celtic Contractors"
            className="partnership-logo"
          />
        </div>
      </div>
    </section>
  );
});

export default Services;
